import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { landingFor } from '../../routes/landing';

// What the shell shows when a page cannot be shown (wf-shell-state 5–8, YES-262): one card with a
// heading, a sentence and the next step. The wording is the wireframes'.
const SystemState = ({ title, children, actions }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 4, sm: 8 }, pb: 6 }}>
        <Paper
            component="section"
            aria-labelledby="system-state-title"
            sx={{ width: '100%', maxWidth: 600, p: { xs: 3, sm: 4 } }}
        >
            <Typography id="system-state-title" variant="h4" component="h1">
                {title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
                {children}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                {actions}
            </Stack>
        </Paper>
    </Box>
);

// 5 · The role lacks the page's permission, or a call on the page returned 403. Never names the
// permission or says what the page holds.
export const AccessDenied = () => {
    const { can } = useAuth();
    const landing = landingFor(can);
    return (
        <SystemState
            title="You don't have access to this page"
            actions={<Button variant="contained" component={RouterLink} to={landing.path}>Go to {landing.label}</Button>}
        >
            Your account can't open this page. If you think it should, ask the person who invited you.
        </SystemState>
    );
};

// 6 · Unknown route, or a 404 for a lesson, run or item. One message for "does not exist" and "not yours".
export const NotFound = () => {
    const { can } = useAuth();
    const landing = landingFor(can);
    return (
        <SystemState
            title="Page not found"
            actions={<Button variant="contained" component={RouterLink} to={landing.path}>Go to {landing.label}</Button>}
        >
            This page doesn't exist, or it isn't yours to open.
        </SystemState>
    );
};

// 7 · /api/auth/me or a later call returned 401. After sign-in the user comes back to this URL.
export const SessionExpired = () => {
    const { logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const signInAgain = async () => {
        const back = `${location.pathname}${location.search}${location.hash}`;
        await logout({ cause: null }); // the session is already gone
        navigate(`/login?redirect_url=${encodeURIComponent(window.location.origin + back)}`);
    };

    return (
        <SystemState
            title="Your session has ended"
            actions={<Button variant="contained" onClick={signInAgain}>Sign in again</Button>}
        >
            Sign in again to carry on. You'll come back to this page.
        </SystemState>
    );
};

// 8 · /api/auth/me failed with no answer or a 5xx. Try again repeats /me; Sign out ends the Clerk session.
export const CouldNotLoad = () => {
    const { retry, logout } = useAuth();
    const navigate = useNavigate();

    const signOut = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <SystemState
            title="Can't reach ADAS right now"
            actions={(
                <>
                    <Button variant="contained" onClick={retry}>Try again</Button>
                    <Button variant="outlined" onClick={signOut}>Sign out</Button>
                </>
            )}
        >
            Check your connection, then try again.
        </SystemState>
    );
};

export default SystemState;
